import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllFeatureConfigs } from "../config/featureDisplayConfig";

const SystemHeader = ({
    userInfo,
    onLogout,
    totalPoints = 0,
    pageTitle = "MLM Form Portal",
    pageSubtitle = "Hệ thống quản lý chiến dịch",
    extraInfo = null, // Additional info to display in the middle section
    onRefreshPoints = null, // Function to manually refresh points (for debugging)
}) => {
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const handleLogoClick = () => {
        navigate("/dashboard");
    };

    const handleNavigate = (path) => {
        navigate(path);
    };

    const getCurrentPath = () => {
        return window.location.pathname;
    };

    // Get first 3 features from config sorted by order
    const featureConfigs = getAllFeatureConfigs();
    const topFeatures = featureConfigs.filter(f => f.enabled !== false).slice(0, 3);

    // Display name: code-facebook (like admin portal)
    const getDisplayName = () => {
        console.log('getDisplayName: ', userInfo);
        const code = userInfo?.code || userInfo?.id;
        const name = userInfo?.facebook || userInfo?.name || '';
        return `${code}-${name}`;
    };

    return (
        <nav className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center py-4">
                    {/* Left: Logo & App Info */}
                    <div className="flex items-center">
                        <button
                            onClick={handleLogoClick}
                            className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mr-3 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105 cursor-pointer shadow-sm"
                            title="Về trang chủ"
                        >
                            <img src="/images/logo-yodywhite.png" alt="Yody Logo" className="w-8 h-8 object-contain" />
                        </button>
                        <div>
                            <button
                                onClick={handleLogoClick}
                                className="text-left hover:text-brand-600 transition-colors cursor-pointer"
                                title="Về trang chủ"
                            ></button>
                        </div>
                    </div>

                    {/* Center: Navigation + Extra Info (Desktop) / User Info (Mobile) */}
                    <div className="flex items-center justify-center flex-1 mx-4">
                        {/* Desktop Navigation - dynamic from featureDisplayConfig */}
                        <div className="hidden md:flex items-center space-x-6">
                            <div className="flex space-x-2">
                                {/* Mục "0. Cập nhật thông tin" — vào form cập nhật hồ sơ đại lý */}
                                <button
                                    onClick={() => handleNavigate('/cap-nhat-thong-tin')}
                                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                        getCurrentPath() === '/cap-nhat-thong-tin'
                                            ? 'bg-brand-600 text-white shadow-md'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    <span className="text-xs opacity-60 mr-1">0.</span>Cập nhật thông tin
                                </button>
                                {topFeatures.map((feature, idx) => (
                                    <button
                                        key={feature.key}
                                        onClick={() => handleNavigate(feature.path)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                            getCurrentPath() === feature.path || getCurrentPath().startsWith(feature.path + "/")
                                                ? "bg-brand-600 text-white shadow-md"
                                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                        }`}
                                    >
                                        <span className="text-xs opacity-60 mr-1">{feature.order || idx + 1}.</span>
                                        {feature.name}
                                    </button>
                                ))}
                            </div>

                            {/* Extra Info */}
                            {extraInfo && <div className="border-l border-gray-200 pl-6">{extraInfo}</div>}
                        </div>

                        {/* Mobile User Info (Center) */}
                        <div className="md:hidden flex items-center space-x-3">
                            {/* Total Points - clickable */}
                            <div className="text-center cursor-pointer active:scale-95 transition-transform" onClick={() => navigate('/diem-so')}>
                                <p className="text-xs font-medium text-brand-600">
                                    {totalPoints.toLocaleString()} điểm
                                </p>
                            </div>

                            {/* User Info */}
                            <div className="text-center">
                                <p className="text-xs font-medium text-gray-900">
                                    {getDisplayName()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right: User Info (Desktop) / Menu Button (Mobile) */}
                    <div className="flex items-center">
                        {/* Desktop User Info & Actions */}
                        <div className="hidden md:flex items-center space-x-4">
                            {/* Total Points - clickable */}
                            <div className="text-right cursor-pointer active:scale-95 transition-transform" onClick={() => navigate('/diem-so')}>
                                <div className="flex items-center space-x-2">
                                    <div>
                                        <p className="text-sm font-medium text-brand-600">
                                            {totalPoints.toLocaleString()} điểm
                                        </p>
                                        <p className="text-xs text-gray-500">Tổng điểm tích lũy</p>
                                    </div>
                                    {/* Debug refresh button (only in development) */}
                                    {process.env.NODE_ENV === "development" && onRefreshPoints && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onRefreshPoints(); }}
                                            className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded transition-colors"
                                            title="Refresh points (Dev only)"
                                        >
                                            🔄
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* User Info */}
                            <div className="text-right">
                                <p className="text-sm font-medium text-gray-900">
                                    {getDisplayName()}
                                </p>
                                <p className="text-xs text-gray-500">Đã đăng nhập</p>
                            </div>

                            {/* Logout Button */}
                            <button
                                onClick={onLogout}
                                className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Đăng xuất
                            </button>
                        </div>

                        {/* Mobile Menu Button */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500"
                                aria-expanded="false"
                            >
                                <span className="sr-only">Mở menu</span>
                                {!isMobileMenuOpen ? (
                                    <svg
                                        className="block h-6 w-6"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 6h16M4 12h16M4 18h16"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className="block h-6 w-6"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Menu - dynamic from featureDisplayConfig */}
            {isMobileMenuOpen && (
                <div className="md:hidden">
                    <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200 shadow-lg">
                        {/* Mục "0. Cập nhật thông tin" — đầu menu, vào form cập nhật hồ sơ */}
                        <button
                            onClick={() => { handleNavigate('/cap-nhat-thong-tin'); setIsMobileMenuOpen(false); }}
                            className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                                getCurrentPath() === '/cap-nhat-thong-tin'
                                    ? 'bg-brand-600 text-white shadow-md'
                                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                        >
                            <div className="flex items-center space-x-2">
                                <span className="material-symbols-rounded w-5 h-5 text-center" style={{ fontSize: '20px' }}>manage_accounts</span>
                                <span><span className="opacity-60 text-sm mr-1">0.</span>Cập nhật thông tin</span>
                            </div>
                        </button>
                        {topFeatures.map((feature, idx) => (
                            <button
                                key={feature.key}
                                onClick={() => {
                                    handleNavigate(feature.path);
                                    setIsMobileMenuOpen(false);
                                }}
                                className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                                    getCurrentPath() === feature.path || getCurrentPath().startsWith(feature.path + "/")
                                        ? "bg-brand-600 text-white shadow-md"
                                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                                }`}
                            >
                                <div className="flex items-center space-x-2">
                                    <span className="material-symbols-rounded w-5 h-5 text-center" style={{ fontSize: '20px' }}>{feature.icon}</span>
                                    <span><span className="opacity-60 text-sm mr-1">{feature.order || idx + 1}.</span>{feature.name}</span>
                                </div>
                            </button>
                        ))}

                        {/* Mobile Logout */}
                        <div className="border-t border-gray-200 pt-4 pb-3">
                            <div className="px-2">
                                <button
                                    onClick={() => {
                                        onLogout();
                                        setIsMobileMenuOpen(false);
                                    }}
                                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                                >
                                    <div className="flex items-center space-x-2">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                            />
                                        </svg>
                                        <span>Đăng xuất</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default SystemHeader;
